import React from 'react'
import { useNavigate } from 'react-router-dom'
import { FormTemplate } from '@/core/models/form.model'
import {
    useSearchFormTemplatesQuery,
    useCreateFormTemplateMutation,
    useUpdateFormTemplateMutation,
    useArchiveFormTemplateMutation,
    useMakePdfFillableMutation,
    useDetectFieldsMutation,
    useBurnPdfMutation,
} from '@/core/services/form'
import { useAddFileMutation, useDeleteFilesMutation } from '@/core/services/file'
import FormTemplatesListComponent from '../components/FormTemplatesList'
import FormTemplateSearchContainer from './FormTemplateSearchContainer'
import CreateFormTemplateDialogComponent from '../components/CreateFormTemplateDialog'
import EditFormTemplateDialogComponent from '../components/EditFormTemplateDialog'
import PreviewFormDialogComponent from '../components/PreviewFormDialog'
import { PaginationState } from '@tanstack/react-table'
import { objectTypes } from '@/core/models/document.model'

interface SearchOptions {
    searchText?: string
    categories?: string[]
    tags?: string[]
    isActive?: boolean
}

const DEFAULT_PAGINATION: PaginationState = {
    pageIndex: 0,
    pageSize: 5,
}

const FormTemplatesContainer: React.FC = () => {
    const navigate = useNavigate()
    const [searchOptions, setSearchOptions] = React.useState<SearchOptions>({
        searchText: '',
        isActive: true,
    })

    const [{ pageIndex, pageSize }, setPagination] = React.useState<PaginationState>(DEFAULT_PAGINATION)

    const { data: templatesResponse, isLoading } = useSearchFormTemplatesQuery({
        search: searchOptions,
        skip: pageIndex * pageSize,
        limit: pageSize,
        orderBy: 'createdAt',
        order: 'DESC'
    })

    const [createTemplate, { isLoading: isCreating }] = useCreateFormTemplateMutation()
    const [updateTemplate, { isLoading: isUpdating }] = useUpdateFormTemplateMutation()
    const [archiveTemplate] = useArchiveFormTemplateMutation()

    const [isCreateDialogOpen, setIsCreateDialogOpen] = React.useState(false)
    const [isEditDialogOpen, setIsEditDialogOpen] = React.useState(false)
    const [isPreviewDialogOpen, setIsPreviewDialogOpen] = React.useState(false)
    const [selectedTemplate, setSelectedTemplate] = React.useState<FormTemplate | null>(null)

    const handleSearchChange = (newSearchOptions: Partial<SearchOptions>) => {
        setSearchOptions(prev => ({
            ...prev,
            ...newSearchOptions,
        }))
        // Reset pagination when search changes
        setPagination(prev => ({ ...prev, pageIndex: 0 }))
    }

    const handlePageChange = (page: number, itemsPerPage: number) => {
        setPagination({
            pageIndex: page - 1, // Convert 1-based to 0-based
            pageSize: itemsPerPage
        })
    }

    const handleItemsPerPageChange = (newPageSize: number) => {
        setPagination({
            pageIndex: 0,
            pageSize: newPageSize
        })
    }

    const handleCreateTemplate = async (data: { title: string; description: string; fields: any[]; categories?: string[]; tags?: string[] }) => {
        try {
            await createTemplate({
                title: data.title,
                description: data.description,
                fields: data.fields.map(field => ({
                    ...field,
                    id: crypto.randomUUID(),
                })),
                categories: data.categories,
                tags: data.tags,
                isActive: true,
            }).unwrap()
            setIsCreateDialogOpen(false)
        } catch (error) {
            console.error('Error creating template:', error)
        }
    }

    const handleEditTemplate = async (data: { title: string; description: string; fields: any[]; aiOutputConfig?: any; categories?: string[]; tags?: string[] }, shouldClose = true) => {
        if (!selectedTemplate) return

        try {
            console.log('Updating template with data:', data)
            await updateTemplate({
                id: selectedTemplate._id,
                changes: {
                    title: data.title,
                    description: data.description,
                    categories: data.categories,
                    tags: data.tags,
                    fields: data.fields.map(field => ({
                        ...field,
                        id: field.id || crypto.randomUUID(),
                        _id: undefined,
                        category: undefined
                    })),
                    aiOutputConfig: data.aiOutputConfig ? {
                        ...data.aiOutputConfig,
                        pdfTemplate: data.aiOutputConfig.pdfTemplate ? {
                            ...data.aiOutputConfig.pdfTemplate,
                            uploadedBy: undefined,
                            url: undefined
                        } : undefined,
                        _id: undefined
                    } : undefined,
                    // We need to preserve pdfFields if they are not passed in data (which they aren't in the main submit)
                    // But if they ARE passed (from workflow), we should update them.
                    // The DTO in handleEditTemplate doesn't explicitly include pdfFields in the signature above, 
                    // but we can cast or add it.
                    pdfFields: (data as any).pdfFields
                },
            }).unwrap()

            if (shouldClose) {
                setIsEditDialogOpen(false)
                setSelectedTemplate(null)
            }
        } catch (error) {
            console.error('Error updating template:', error)
        }
    }

    const handleArchiveTemplate = async (template: FormTemplate) => {
        try {
            await archiveTemplate(template._id).unwrap()
        } catch (error) {
            console.error('Error archiving template:', error)
        }
    }

    const [addFile] = useAddFileMutation()
    const [deleteFiles] = useDeleteFilesMutation()

    const handleUploadPdf = async (file: File, templateId: string) => {
        return await addFile({
            file,
            objectType: objectTypes.FORM_TEMPLATE,
            objectId: templateId
        }).unwrap()
    }

    const handleDeletePdf = async (file: any, templateId: string) => {
        await deleteFiles({
            files: [file],
            objectType: objectTypes.FORM_TEMPLATE,
            objectId: templateId
        }).unwrap()
    }

    const [detectFields] = useDetectFieldsMutation()
    const [burnPdf] = useBurnPdfMutation()

    const handleDetectFields = async (templateId: string) => {
        return await detectFields(templateId).unwrap()
    }

    const handleBurnPdf = async (templateId: string) => {
        return await burnPdf(templateId).unwrap()
    }

    const handleOpenEditDialog = (template: FormTemplate) => {
        setSelectedTemplate(template)
        setIsEditDialogOpen(true)
    }

    const handleOpenPreviewDialog = (template: FormTemplate) => {
        setSelectedTemplate(template)
        setIsPreviewDialogOpen(true)
    }

    const handleViewTemplate = (template: FormTemplate) => {
        navigate(`/admin/forms/${template._id}`)
    }

    const handleCloseEditDialog = () => {
        setIsEditDialogOpen(false)
        setSelectedTemplate(null)
    }

    const handleClosePreviewDialog = () => {
        setIsPreviewDialogOpen(false)
        setSelectedTemplate(null)
    }

    return (
        <>
            <FormTemplateSearchContainer
                value={searchOptions}
                onChange={handleSearchChange}
            />

            <FormTemplatesListComponent
                templates={templatesResponse?.items || []}
                totalCount={templatesResponse?.totalCount || 0}
                page={pageIndex + 1}
                pageSize={pageSize}
                isLoading={isLoading}
                onEdit={handleOpenEditDialog}
                onArchive={handleArchiveTemplate}
                onPreview={handleOpenPreviewDialog}
                onView={handleViewTemplate}
                onCreateNew={() => setIsCreateDialogOpen(true)}
                onPageChange={handlePageChange}
                onPageSizeChange={handleItemsPerPageChange}
            />

            <CreateFormTemplateDialogComponent
                open={isCreateDialogOpen}
                onClose={() => setIsCreateDialogOpen(false)}
                onSubmit={handleCreateTemplate}
                isSubmitting={isCreating}
            />

            {selectedTemplate && (
                <>
                    <EditFormTemplateDialogComponent
                        open={isEditDialogOpen}
                        onClose={handleCloseEditDialog}
                        onSubmit={handleEditTemplate}
                        template={selectedTemplate}
                        isSubmitting={isUpdating}
                        onUploadPdf={handleUploadPdf}
                        onDeletePdf={handleDeletePdf}
                        onDetectFields={handleDetectFields}
                        onBurnPdf={handleBurnPdf}
                    />

                    <PreviewFormDialogComponent
                        open={isPreviewDialogOpen}
                        onClose={handleClosePreviewDialog}
                        template={selectedTemplate}
                    />
                </>
            )}
        </>
    )
}

export default FormTemplatesContainer 